import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { ApiService } from '../../../../../core/services/api.service';
import { apiBaseUrlFor } from '../../../../../core/services/prism-new-routing';
import { DeckProductContext } from '../../deck-admin.models';
import {
  AgentPrompt,
  AgentPromptListResponse,
  AgentPromptVersion,
  AgentPromptVersionListResponse,
  CreateAgentPromptPayload,
  PatchAgentPromptPayload,
  SavePromptVersionPayload,
} from './agent-prompt.models';

/**
 * HTTP client for the live-call agent's named prompts.
 *
 * ── BACKEND CONTRACT (prism-backend-nodejs, live-call-management/agent) ─────
 * Routes mirror the deck-admin convention: product-scoped by a `:product` path
 * segment (pid or keyname), served by the CRM Node backend under `/api/v1`.
 * Prompts are addressed by numeric id.
 *
 *   GET    /live-call-agent/:product/prompts?includeInactive=0|1
 *            → { product, count, prompts: AgentPrompt[] }   (previews only)
 *   POST   /live-call-agent/:product/prompts
 *            body: { name, system_prompt, model_id?, is_active? }
 *            → { prompt: AgentPrompt }   (created in this deployment's env)
 *   GET    /live-call-agent/:product/prompts/:id
 *            → { prompt: AgentPrompt }   (full systemPrompt text included)
 *   PATCH  /live-call-agent/:product/prompts/:id      (metadata ONLY)
 *            body: { is_active?, for_env? }
 *            → { prompt: AgentPrompt }
 *   DELETE /live-call-agent/:product/prompts/:id
 *            → { deleted: boolean }
 *
 * Content changes go through the VERSION routes — every save appends an immutable
 * snapshot with a required "what changed" note; restore = old snapshot as a NEW version:
 *
 *   GET    …/prompts/:id/versions                     → AgentPromptVersionListResponse
 *   POST   …/prompts/:id/versions                     body: SavePromptVersionPayload
 *   GET    …/prompts/:id/versions/:version            → { version } (full text, for diff)
 *   POST   …/prompts/:id/versions/:version/restore    body: { change_notes? }
 */
@Injectable({
  providedIn: 'root',
})
export class AgentPromptService {
  private api = inject(ApiService);

  /** Product scope for every request; empty until the page resolves a product. */
  private productContext: DeckProductContext = { productKey: null, productPid: null };

  /** Suppress ApiService's global toaster/loader — the tab renders its own states. */
  private readonly quiet = { showErrorToaster: false, showLoader: false } as const;

  /** Set which product's prompts subsequent calls address. */
  setProductContext(context: DeckProductContext): void {
    this.productContext = context;
  }

  /** The `:product` path segment — keyname preferred, pid as fallback. */
  private productSegment(): string | null {
    const { productKey, productPid } = this.productContext;
    if (productKey) return productKey;
    if (productPid != null) return String(productPid);
    return null;
  }

  /** Route prefix for the selected product, or null when no product is set. */
  private basePath(): string | null {
    const segment = this.productSegment();
    return segment ? `/live-call-agent/${encodeURIComponent(segment)}/prompts` : null;
  }

  private noProduct(): Observable<never> {
    return throwError(() => ({
      message: 'No product selected — pick a product from the sidebar first.',
    }));
  }

  /** GET the product's prompts. */
  listPrompts(includeInactive = true): Observable<AgentPromptListResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    const params: Record<string, string | number> = {};
    if (!includeInactive) params['includeInactive'] = 0;
    return this.api
      .get<AgentPromptListResponse>(base, params, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** GET one prompt with its FULL system prompt text — what the edit form loads. */
  getPrompt(promptId: number): Observable<{ prompt: AgentPrompt }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .get<{ prompt: AgentPrompt }>(`${base}/${promptId}`, {}, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST a new prompt — created visible only in this deployment's env. */
  createPrompt(payload: CreateAgentPromptPayload): Observable<{ prompt: AgentPrompt }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .post<{ prompt: AgentPrompt }>(base, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** PATCH prompt metadata/text. */
  patchPrompt(promptId: number, payload: PatchAgentPromptPayload): Observable<{ prompt: AgentPrompt }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .patch<{ prompt: AgentPrompt }>(`${base}/${promptId}`, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** GET a prompt's version history (previews only, newest first). */
  listVersions(promptId: number): Observable<AgentPromptVersionListResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .get<AgentPromptVersionListResponse>(`${base}/${promptId}/versions`, {}, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** GET one version with its FULL snapshot text — what the diff/view loads. */
  getVersion(promptId: number, version: number): Observable<{ version: AgentPromptVersion }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .get<{ version: AgentPromptVersion }>(`${base}/${promptId}/versions/${version}`, {}, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST the edited prompt as a NEW version (change_notes required). */
  saveVersion(promptId: number, payload: SavePromptVersionPayload): Observable<{ prompt: AgentPrompt }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    const url = `${base}/${promptId}/versions`;
    // prism-new snapshots the prompt as it stands (its versions body is change_notes only) and takes
    // content edits on PATCH: save the edited content first, then snapshot it.
    if (apiBaseUrlFor('POST', url) !== environment.apiUrl) {
      const { change_notes, ...content } = payload;
      return this.api.patch<{ prompt: AgentPrompt }>(`${base}/${promptId}`, content, this.quiet).pipe(
        switchMap((saved) => this.api.post(url, { change_notes }, this.quiet).pipe(map(() => saved))),
        catchError(this.handleError),
      );
    }
    return this.api
      .post<{ prompt: AgentPrompt }>(url, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST a restore — the old snapshot becomes a NEW version; nothing is deleted. */
  restoreVersion(promptId: number, version: number): Observable<{ prompt: AgentPrompt; restoredFrom: number }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .post<{ prompt: AgentPrompt; restoredFrom: number }>(`${base}/${promptId}/versions/${version}/restore`, {}, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** DELETE one prompt. */
  deletePrompt(promptId: number): Observable<{ deleted: boolean }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .delete<{ deleted: boolean }>(`${base}/${promptId}`, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** Normalize ApiService errors to the `{status?, message}` the tab renders. */
  private handleError = (error: any): Observable<never> => {
    const message =
      error?.message ||
      error?.error ||
      error?.err ||
      error?.detail ||
      'An unexpected error occurred';
    return throwError(() => ({ status: error?.statusCode ?? error?.status, message, raw: error }));
  };
}
